// package
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

//---------------------------------------------------------------------------
// custom
import { usePageLayout } from '@/app/router';
import VectorOk from '@shared/assets/quiz/check2-square_x2.png';
import VectorNg from '@shared/assets/quiz/Vector_x2.png';
import { post35QuizList, post36QuizAnswer } from '../api/quizService';
import { ROUTES } from '@/app/router/routes';
import CloseButton from '@shared/components/CloseButton';
import { useLanguage } from '@shared/contexts/LanguageContext';

//---------------------------------------------------------------------------
const QuizQuestionPage = () => {
  // /quiz/:id → chapterId
  const { id: chapterId } = useParams();
  const navigate = useNavigate();

  const { setLayoutOptions } = usePageLayout();
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage.code === 'en';
  const titleRef = useRef(null);
  const [titleHeight, setTitleHeight] = useState(0);
  useEffect(() => {
    if (!titleRef.current) return;
    const observer = new ResizeObserver(() => {
      setTitleHeight(titleRef.current?.offsetHeight ?? 0);
    });
    observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    setLayoutOptions({ hideHeader: true, hideBottomNav: true, hideFooter: true });
    return () => setLayoutOptions({});  // 離開頁面時還原
  }, [setLayoutOptions]);

  // 預先載入結果與下一題圖示，避免首次顯示時閃爍
  useEffect(() => {
    const arrPreload = [VectorOk, VectorNg];
    const arrImage = arrPreload.map((src) => {
      const img = new Image();
      img.src = src;
      return img;
    });

    return () => { arrImage.forEach((img) => { img.src = ''; }) }
  }, []);


  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [answerState, setAnswerState] = useState(null); // null | 'correct' | 'wrong'
  const [isAnswerSubmitting, setIsAnswerSubmitting] = useState(false);
  const [correctAnswerText, setCorrectAnswerText] = useState('');
  const [answerResults, setAnswerResults] = useState([]); // Array<null | 'correct' | 'wrong'>：每題作答結果

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    setQuiz(null);
    setCurrentIndex(0);
    setSelectedOptionId(null);
    setAnswerState(null);
    setCorrectAnswerText('');
    setAnswerResults([]);
    setIsAnswerSubmitting(false);

    const is_question_random = false;
    const is_option_random = false;

    post35QuizList(chapterId, is_question_random, is_option_random)
      .then((res) => {
        if (!isMounted) return;
        const data = res?.data ?? res ?? {};
        setQuiz(data);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error('Failed to load quiz list:', err);
        setError(err);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [chapterId]);

  const answeredItems = useMemo(() => {
    const a = quiz?.answered_items ?? quiz?.data?.answered_items ?? [];
    return Array.isArray(a) ? a : [];
  }, [quiz]);

  const questions = useMemo(() => {
    const items = quiz?.items ?? quiz?.data?.items ?? [];
    return Array.isArray(items) ? items : [];
  }, [quiz]);

  const currentQuestion = questions[currentIndex] ?? null;
  const total =
    Number(quiz?.total_question) > 0
      ? Number(quiz?.total_question)
      : answeredItems.length + questions.length;
  const displayIndex =
    currentQuestion?.number ??
    Math.min(currentIndex + 1, Math.max(total, 1));

  // 依 API 的 total_question 與 answered_items[].is_correct 初始化進度（可接續作答）
  useEffect(() => {
    if (!quiz) return;
    const n =
      Number(quiz.total_question) > 0
        ? Number(quiz.total_question)
        : answeredItems.length + questions.length;
    if (!n) {
      setAnswerResults([]);
      return;
    }
    setAnswerResults(
      Array.from({ length: n }, (_, i) => {
        const answered = answeredItems.find((a) => Number(a.number) === i + 1);
        if (answered) return answered.is_correct ? 'correct' : 'wrong';
        return null;
      }),
    );
  }, [quiz, answeredItems, questions.length]);

  const getOptionId = (opt) => opt?.option_id ?? opt?.id;
  const getOptionContent = (opt) => opt?.content ?? opt?.text ?? '';
  const onClickSelectOption = async (optionId) => {
    if (!currentQuestion) return;
    if (isAnswerSubmitting || answerState) return; // 已作答中/已作答完成：不允許再選

    setSelectedOptionId(optionId);
    setIsAnswerSubmitting(true);

    try {
      const questionId =
        currentQuestion?.question_id ??
        currentQuestion?.id ??
        currentQuestion?.questionId;

      const res = await post36QuizAnswer(questionId, optionId);
      const data = res?.data ?? res ?? {};

      // API 回傳欄位 key 集中管理：
      // 變數名稱固定，之後只需改「值」即可對應 API 欄位變更。
      const API_KEYS = {
        ANSWER: 'answer',
        OPTION_ID: 'option_id',
        CONTENT: 'content',
        ANSWER_CORRECT: 'answer_correct',
      };

      // 將後續變數統一用 questionAnswer 結構
      const optionList = currentQuestion?.option ?? [];

      const answer = data?.[API_KEYS.ANSWER] ?? {};
      const correctId = answer?.[API_KEYS.OPTION_ID] ?? null;
      const correctAnswerFromResp = answer?.[API_KEYS.CONTENT] ?? null;
      const isCorrectRaw = data?.[API_KEYS.ANSWER_CORRECT];

      const isCorrectByFlag = typeof isCorrectRaw === 'boolean' ? isCorrectRaw : null;
      const isCorrectByOptionId =
        correctId != null ? Number(optionId) === Number(correctId) : null;

      const isCorrect = isCorrectByFlag ?? (isCorrectByOptionId ?? false);

      const correctText =
        correctAnswerFromResp ??
        (correctId != null
          ? getOptionContent(
            optionList.find((opt) => getOptionId(opt) === correctId),
          )
          : '');

      setCorrectAnswerText(correctText);
      setAnswerState(isCorrect ? 'correct' : 'wrong');
      setAnswerResults((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const slot =
          Number(currentQuestion?.number) > 0
            ? Number(currentQuestion.number) - 1
            : currentIndex;
        next[slot] = isCorrect ? 'correct' : 'wrong';
        return next;
      });
    } catch (err) {
      console.error('Failed to submit quiz answer:', err);
      setAnswerState('wrong');
      setAnswerResults((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const slot =
          Number(currentQuestion?.number) > 0
            ? Number(currentQuestion.number) - 1
            : currentIndex;
        next[slot] = 'wrong';
        return next;
      });
    } finally {
      setIsAnswerSubmitting(false);
    }
  };


  const onClickBack = () => {
    navigate(-2);
  };

  const onClickNext = () => {
    const remainingCount = questions.length;
    if (remainingCount === 0 || currentIndex > remainingCount - 1) return;
    if (currentIndex === remainingCount - 1) {
      // 將本次作答結果帶到下一頁，讓答案頁可計算「答對題數/分數」
      navigate(ROUTES.QUIZ_ANSWER, {
        state: {
          chapterId: chapterId != null ? Number(chapterId) : chapterId,
          quizContent: quiz?.content ?? '中台知多少',
          total,
          answerResults,
        },
      });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });

    setCurrentIndex((v) => v + 1);
    setSelectedOptionId(null);
    setAnswerState(null);
    setCorrectAnswerText('');
    setIsAnswerSubmitting(false);
  };

  //---------------------------------------------------------------------------
  // 「對/錯」區塊：相對／flex 排版，跟隨題幹與選項，避免小螢幕長題幹時與選項重疊
  const QUIZ_RESULT_SLOT_MIN_HEIGHT = 100;
  const NEXT_BTN_SIZE = 80;
  const NEXT_BTN_ROW_MARGIN_TOP = 0;

  const QuizResultOk = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 48,
      }}
    >
      <div
        data-layer="check2-square"
        className="Check2Square"
        style={{
          width: 40,
          height: 40,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <img src={VectorOk} alt="" />
      </div>
      <div style={{ pointerEvents: 'none' }} aria-hidden="true">
        <div
          style={{
            color: '#2A9D4E',
            fontSize: 22,
            fontWeight: 700,
            wordWrap: 'break-word',
          }}
        >
          {t('quiz-question.correctTitle')}
        </div>
      </div>
    </div>
  );

  const QuizResultNg = ({ correctText }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minHeight: 48,
        }}
      >
        <div
          data-layer="Container"
          style={{
            width: 28,
            height: 41,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <img src={VectorNg} alt="" />
        </div>
        <div style={{ pointerEvents: 'none' }} aria-hidden="true">
          <div
            style={{
              color: '#E20020',
              fontSize: 22,
              // fontFamily: 'Noto Serif TC',
              fontWeight: 700,
              wordWrap: 'break-word',
            }}
          >
            {t('quiz-question.wrongTitle')}
          </div>
        </div>
      </div>

      <div
        data-layer="Group34508"
        style={{
          // background: '#008',
          paddingLeft: 40,
          display: 'block',
          pointerEvents: 'none',
          color: '#E20020',
          fontSize: 24,
          // fontFamily: 'Noto Sans TC',
          fontWeight: 500,
          lineHeight: '24px',
          wordBreak: 'keep-all',
          whiteSpace: 'normal',
        }}
        aria-hidden="true"
      >
        <span style={{ whiteSpace: 'nowrap' }}>{t('quiz-question.correctAnswerPrefix')}</span>
        <br />
        {correctText}
      </div>
    </div>
  );

  const headerGradientStyle = {
    background: 'linear-gradient(180deg, #4DC8E0 0%, #0097B2 100%)',
    boxShadow: '0px 4px 30px rgba(88.73, 187.23, 206.17, 0.50)',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  };

  const containerStyle = {
    width: '100%',
    maxWidth: 430,
    minHeight: 932,
    position: 'relative',
    background: '#F7F8FA',
    margin: '0 auto',
    paddingBottom: 40,
    boxSizing: 'border-box',

    // background: '#f00',
  };

  //---------------------------------------------------------------------------
  return (
    <section style={containerStyle}>
      {/* Header */}
      <div
        style={{
          width: '100%',
          height: 144,
          paddingTop: 33.5,
          paddingBottom: 33.5,
          left: 0,
          top: -1,
          position: 'absolute',
          ...headerGradientStyle,
        }}
      />

      {/* 右上角關閉/返回按鈕 */}
      <CloseButton onClick={onClickBack} />

      {/* 標題 */}
      {/* 中台知多少 */}
      <div
        ref={titleRef}
        style={{
          width: '66.5%',
          maxWidth: 286,
          // left: 49,
          left: 21,
          top: 32,
          position: 'absolute',
          justifyContent: 'center',
          display: 'flex',
          flexDirection: 'column',
          color: 'white',
          fontWeight: 900,
          lineHeight: isEn ? '22px' : '40px',
          wordWrap: 'break-word',
          // background: '#f00',
          gap: isEn ? 4 : 10,
        }}
      >
        <span style={{ fontSize: isEn ? 17 : 30 }}>{t('quiz.heroTitle')}</span>
        <span style={{ fontSize: isEn ? 15 : 28, ...(isEn && { maxWidth: '75%', display: 'block' }) }}>{quiz?.content}</span>
      </div>

      {/* 題數進度 Ellipse（Ellipse912~Ellipse919） */}
      {!loading && !error && (
        <>
          {Array.from({ length: Math.max(total, 1) }).map((_, idx) => {
            const ellipseRight = 21 + Math.max(total - 1 - idx, 0) * 20;
            const status = answerResults[idx] ?? null; // null | 'correct' | 'wrong'
            const background =
              status === 'correct'
                ? '#3AD162' // 對應佛學會考2_ok 的 Ellipse920
                : status === 'wrong'
                  ? '#FF0000' // 對應佛學會考2_ng 的 Ellipse912
                  : '#D9D9D9';

            return (
              <div
                // idx=0 對應 Ellipse920/Ellipse912 的第一顆
                key={idx}
                style={{
                  position: 'absolute',
                  width: 14,
                  height: 14,
                  right: ellipseRight,
                  top: 97,
                  background,
                  boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.25)',
                  borderRadius: 9999,
                  zIndex: 1,
                }}
                aria-hidden="true"
              />
            );
          })}
        </>
      )}

      {/* 進度 */}
      {!loading && !error && (
        <div
          style={{
            left: 32,
            top: 275,
            position: 'absolute',
            color: '#8A8278',
            fontSize: 13,
            fontWeight: 400,
            wordWrap: 'break-word',
          }}
        >
          {t('quiz-question.progress').replace('{{current}}', displayIndex).replace('{{total}}', Math.max(total, 0))}
        </div>
      )}

      {/* 內容區（文件流，高度隨題幹／選項延伸，作答結果接在選項下方） */}
      <div
        style={{
          position: 'relative',
          left: 0,
          right: 0,
          paddingTop: isEn ? Math.max(156, 32 + titleHeight + 16) : 156,
          paddingLeft: 24,
          paddingRight: 24,
        }}
      >
        {loading && (
          <div style={{ color: '#6E625B', fontSize: 18, fontWeight: 600 }}>
            {t('quiz.loading')}
          </div>
        )}

        {!loading && error && (
          <div style={{ color: '#6E625B', fontSize: 18, fontWeight: 600 }}>
            {t('quiz-question.loadError')}
          </div>
        )}

        {!loading && !error && !currentQuestion && (
          <div style={{ color: '#6E625B', fontSize: 18, fontWeight: 600 }}>
            {t('quiz-question.noQuestions')}
          </div>
        )}

        {!loading && !error && currentQuestion && (
          <div style={{ paddingTop: 22 }}>
            <div style={{ position: 'relative', minHeight: isEn ? 225 : 256 }}>
              {/* 題號 */}
              <div
                style={{
                  // background: '#f00',
                  width: 120,
                  height: 70,
                  position: 'absolute',
                  left: 0,
                  top: 16,
                  color: '#4DC8E0',
                  fontSize: 96,
                  fontFamily: 'Times New Roman',
                  fontWeight: 700,
                  lineHeight: '70px',
                  wordWrap: 'break-word',
                  textAlign: 'center',
                }}
              >
                {displayIndex}
              </div>

              {/* 題目 */}
              <div
                style={{
                  // background: '#080',
                  width: 'calc(100% - 120px)',
                  minHeight: 120,
                  marginLeft: 120,
                  paddingTop: 6,
                  color: '#6E625B',
                  fontSize: isEn ? 16 : 24,
                  fontWeight: 500,
                  // lineHeight: '38px',
                  lineHeight: isEn ? '24px' : '32px',
                  wordWrap: 'break-word',
                }}
              >
                {currentQuestion.content}
              </div>
            </div>

            {/* 選項 */}
            <div
              style={{
                marginTop: isEn ? 0 : 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              {(currentQuestion.option ?? []).map((opt) => {
                const optionId = opt.option_id ?? opt.id;
                const selected = selectedOptionId === optionId;
                const canSelect = !isAnswerSubmitting && !answerState;
                const showSelectedGradient = !!answerState && selected;
                const selectedGradientStyle = {
                  background: 'linear-gradient(180deg, #4DC8E0 0%, #0097B2 100%)',
                  border: 'none',
                  color: 'white',
                };
                const normalStyle = {
                  background: 'white',
                  border: 'none',
                  color: '#6E625B',
                };
                return (
                  <button
                    key={optionId}
                    type="button"
                    onClick={() => onClickSelectOption(optionId)}
                    disabled={!canSelect}
                    style={{
                      width: '100%',
                      minHeight: 58,
                      ...(showSelectedGradient ? selectedGradientStyle : normalStyle),
                      boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.20)',
                      borderRadius: 20,
                      border: answerState ? 'none' : selected ? '2px solid #4DC8E0' : '2px solid transparent',
                      paddingLeft: 18,
                      paddingRight: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      color: showSelectedGradient ? 'white' : '#6E625B',
                      fontSize: isEn ? 16 : 22,
                      fontWeight: 600,
                      textAlign: 'left',
                      cursor: canSelect ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {opt.content}
                  </button>
                );
              })}
            </div>

            {/* 作答結果區：未選時保留固定高度空白；顯示對/錯時下一題按鈕列位置不跳動 */}
            <div
              style={{
                marginTop: 28,
                minHeight: QUIZ_RESULT_SLOT_MIN_HEIGHT,
              }}
            >
              {answerState === 'correct' &&
                <QuizResultOk />}
              {answerState === 'wrong' && (
                <QuizResultNg correctText={correctAnswerText} />
              )}
            </div>

            {/* 下一題：固定列高占位，未作答時留白；作答後顯示按鈕（位置與占位相同） */}
            <div
              style={{
                marginTop: NEXT_BTN_ROW_MARGIN_TOP,
                minHeight: NEXT_BTN_SIZE,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
                paddingRight: 8,
              }}
            >
              {answerState ? (
                <button
                  type="button"
                  onClick={onClickNext}
                  aria-label={t('quiz-question.nextAria')}
                  style={{
                    position: 'relative',
                    flexShrink: 0,
                    width: NEXT_BTN_SIZE,
                    height: NEXT_BTN_SIZE,
                    borderRadius: NEXT_BTN_SIZE / 2,
                    border: 'none',
                    padding: 0,
                    background: 'linear-gradient(180deg, #4DC8E0 0%, #0097B2 100%)',
                    boxShadow: '1px 1px 10px rgba(0, 0, 0, 0.50)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ChevronRight size={80} color="#FFFFFF" strokeWidth={2.5} aria-hidden />
                  </div>
                </button>
              ) : (
                <div
                  style={{
                    width: NEXT_BTN_SIZE,
                    height: NEXT_BTN_SIZE,
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default QuizQuestionPage;